import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "aman_admin_token";

export const adminApi = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token on every request
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
  return config;
});

// Auto-redirect to /admin/login on 401
adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (
      err?.response?.status === 401 &&
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/admin") &&
      window.location.pathname !== "/admin/login"
    ) {
      localStorage.removeItem(TOKEN_KEY);
      window.location.href = "/admin/login";
    }
    return Promise.reject(err);
  }
);

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export function extractErrorAr(err) {
  const fallback = "حدث خطأ غير متوقع، حاول مرة أخرى.";
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => d?.msg).filter(Boolean).join("، ") || fallback;
  }
  return fallback;
}

// ---- Endpoints ------------------------------------------------------------
export const adminEndpoints = {
  login: (username, password) =>
    adminApi.post("/auth/login", { username, password }).then((r) => r.data),
  me: () => adminApi.get("/auth/me").then((r) => r.data),
  dashboard: () => adminApi.get("/admin/dashboard").then((r) => r.data),

  // Quote requests
  listQuotes: (params = {}) =>
    adminApi.get("/admin/quote-requests", { params }).then((r) => r.data),
  updateQuoteStatus: (id, status) =>
    adminApi.patch(`/admin/quote-requests/${id}/status`, { status }).then((r) => r.data),

  // Generic CRUD helpers
  crud: (resource) => ({
    list: () => adminApi.get(`/admin/${resource}`).then((r) => r.data),
    create: (data) => adminApi.post(`/admin/${resource}`, data).then((r) => r.data),
    update: (id, data) => adminApi.put(`/admin/${resource}/${id}`, data).then((r) => r.data),
    remove: (id) => adminApi.delete(`/admin/${resource}/${id}`).then((r) => r.data),
  }),

  // Settings (single)
  getSettings: () => adminApi.get("/admin/settings").then((r) => r.data),
  updateSettings: (data) => adminApi.put("/admin/settings", data).then((r) => r.data),
  testWhatsApp: () =>
    adminApi.post("/admin/integrations/whatsapp/test").then((r) => r.data),

  changePassword: (current_password, new_password) =>
    adminApi
      .post("/admin/auth/change-password", { current_password, new_password })
      .then((r) => r.data),

  // Deploy package (downloads a zip via authenticated fetch)
  downloadDeployPackage: async () => {
    return await _downloadAuthed("/api/admin/deploy-package");
  },

  // Backups
  listBackups: () => adminApi.get("/admin/backups").then((r) => r.data),
  createBackup: () => adminApi.post("/admin/backups").then((r) => r.data),
  deleteBackup: (name) =>
    adminApi.delete(`/admin/backups/${encodeURIComponent(name)}`).then((r) => r.data),
  downloadBackup: async (name) =>
    _downloadAuthed(`/api/admin/backups/${encodeURIComponent(name)}/download`),

  // Upload (optionally process: bg removal + dark canvas)
  upload: async (file, { process = false } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (process) fd.append("process", "true");
    const { data } = await adminApi.post("/admin/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
};

// Absolute URL helper for image paths returned by the API (e.g. /api/static/...)
export function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  if (pathOrUrl.startsWith("/")) return `${BACKEND_URL}${pathOrUrl}`;
  return pathOrUrl;
}

// Authenticated file download → triggers browser "Save as"
async function _downloadAuthed(path, defaultName = "download.zip") {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = "فشل تنزيل الملف.";
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
    } catch (_) {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const filename = m ? m[1] : defaultName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { filename, size: blob.size };
}
